import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, Clock, Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

interface Event {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  city: string;
  country: string;
  venue_name: string;
  venue_address: string;
  description: string | null;
  registration_link: string | null;
  focus_tags: string[];
  status: string | null;
  created_at: string;
}

const FOCUS_TAG_COLORS: Record<string, string> = {
  diaspora: 'bg-secondary/10 text-secondary border-secondary/20',
  investors: 'bg-primary/10 text-primary border-primary/20',
  youth: 'bg-spa-minerals/10 text-spa-minerals border-spa-minerals/20',
  academic: 'bg-spa-governance/10 text-spa-governance border-spa-governance/20',
  government: 'bg-foreground/10 text-foreground border-foreground/20',
  civil_society: 'bg-spa-infrastructure/10 text-spa-infrastructure border-spa-infrastructure/20',
};

function FocusTag({ tag }: { tag: string }) {
  const colorClass = FOCUS_TAG_COLORS[tag] || 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${colorClass}`}>
      {tag.replace(/_/g, ' ')}
    </span>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  if (status === 'upcoming') return <Badge className="bg-secondary text-secondary-foreground">Upcoming</Badge>;
  if (status === 'cancelled') return <Badge variant="destructive">Cancelled</Badge>;
  return <Badge variant="secondary">Past</Badge>;
}

function formatTime(time: string) {
  // time is HH:MM:SS or HH:MM — show HH:MM
  return time.slice(0, 5);
}

export default function EventsDirectory() {
  const { t } = useTranslation();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('date', { ascending: true });

      if (error) throw error;
      setEvents(
        (data || []).map((e: any) => ({
          ...e,
          focus_tags: Array.isArray(e.focus_tags) ? e.focus_tags : [],
        }))
      );
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const upcoming = events.filter(e => e.status === 'upcoming');
  const past = events.filter(e => e.status === 'past' || e.status === 'cancelled');

  const renderCards = (list: Event[], emptyMsg: string) => {
    if (loading) {
      return (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6 space-y-3">
                <div className="flex gap-4">
                  <Skeleton className="h-20 w-16 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Skeleton className="h-5 w-14 rounded-full" />
                  <Skeleton className="h-5 w-14 rounded-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    if (list.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <CalendarIcon className="h-16 w-16 text-muted-foreground/40 mb-4" />
          <p className="text-muted-foreground">{emptyMsg}</p>
        </div>
      );
    }

    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {list.map(event => {
          const eventDate = new Date(event.date);
          return (
            <Card key={event.id} className="transition-shadow hover:shadow-lg">
              <CardContent className="p-6">
                <div className="flex gap-4">
                  {/* Date badge */}
                  <div className="flex h-20 w-16 flex-col items-center justify-center rounded-lg bg-primary text-primary-foreground shrink-0">
                    <span className="text-2xl font-bold leading-none">{format(eventDate, 'd')}</span>
                    <span className="text-xs font-medium uppercase">{format(eventDate, 'MMM')}</span>
                    <span className="text-[10px] opacity-80">{format(eventDate, 'yyyy')}</span>
                  </div>

                  <div className="min-w-0 flex-1 space-y-1.5">
                    <h3 className="font-bold leading-tight line-clamp-2">{event.title}</h3>
                    <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Clock className="h-3.5 w-3.5 shrink-0" />
                      {formatTime(event.start_time)} – {formatTime(event.end_time)}
                    </p>
                    <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      {event.city}, {event.country}
                    </p>
                    <p className="text-xs text-muted-foreground">{event.venue_name}</p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-1.5">
                  <StatusBadge status={event.status} />
                  {event.focus_tags.map(tag => (
                    <FocusTag key={tag} tag={tag} />
                  ))}
                </div>
              </CardContent>
              <CardFooter className="border-t p-4">
                <Button asChild variant="outline" className="w-full">
                  <Link to={`/events/${event.id}`}>Details →</Link>
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <Layout>
      <div className="container py-8">
        <h1 className="text-3xl font-bold">{t('events.title')}</h1>
        <p className="mt-2 mb-8 text-muted-foreground">{t('events.description')}</p>

        <Tabs defaultValue="upcoming">
          <TabsList className="mb-6">
            <TabsTrigger value="upcoming">{t('events.upcoming')}</TabsTrigger>
            <TabsTrigger value="past">{t('events.past')}</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {renderCards(upcoming, t('events.noUpcoming'))}
          </TabsContent>
          <TabsContent value="past">
            {renderCards(past, t('events.noPast'))}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
